import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { ShieldCheck, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";

const ROLE_STYLE = {
  super_admin: "bg-indigo-100 text-indigo-700",
  hr_admin: "bg-emerald-100 text-emerald-800",
  manager: "bg-amber-100 text-amber-800",
  employee: "bg-slate-100 text-slate-600",
};

export default function AccessMatrixPage() {
  const { user: me, hasPerm, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState("");

  const load = () => api.get("/users/permissions-matrix").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!hasPerm("roles.manage")) return <Navigate to="/" replace />;

  const toggle = async (u, perm) => {
    const has = u.effective.includes(perm);
    setSaving(`${u.id}-${perm}`);
    try {
      const { data: res } = await api.put(`/users/${u.id}/permissions`, { overrides: { ...u.overrides, [perm]: !has } });
      toast.success(`${u.full_name}: ${data.labels[perm]} ${res.effective.includes(perm) ? "granted" : "removed"}`);
      load();
      if (u.id === me.id) refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving("");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">Security</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
          <ShieldCheck size={30} weight="duotone" className="text-indigo-600" /> Access matrix
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Roles don't grant anything by themselves — every ability is granted per person right here.
          Tap a chip to grant or remove it. Tabs and features hide automatically for anyone without access.
          Super admins always have everything. Every change is audited.
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          <span className="inline-flex items-center gap-1 mr-3"><span className="inline-block w-3 h-3 rounded-full bg-indigo-600" /> has access</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full border border-border bg-card" /> no access</span>
        </p>
      </div>

      {data && (
        <div className="bg-card border border-border rounded-md divide-y divide-border" data-testid="access-matrix">
          {data.users.map((u) => (
            <div key={u.id} data-testid={`access-row-${u.id}`} className="px-4 sm:px-6 py-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Link to={`/team/${u.id}`} className="font-medium hover:text-indigo-600 transition-colors">{u.full_name}</Link>
                <Badge className={ROLE_STYLE[u.role]}>{u.role.replace("_", " ")}</Badge>
                {u.role === "super_admin" && <Badge className="bg-indigo-50 text-indigo-600 border border-indigo-200">always full access</Badge>}
              </div>
              <div className="flex flex-wrap gap-2">
                {data.permissions.map((p) => {
                  const has = u.effective.includes(p);
                  const locked = u.role === "super_admin" || (u.id === me.id && p === "roles.manage");
                  return (
                    <button
                      key={p}
                      data-testid={`access-${u.id}-${p}`}
                      disabled={locked || saving === `${u.id}-${p}`}
                      onClick={() => toggle(u, p)}
                      title={locked ? "Locked" : has ? "Granted — tap to remove" : "Tap to grant"}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors
                        ${has ? "bg-indigo-600 border-indigo-600 text-white" : "bg-card border-border text-muted-foreground"}
                        ${locked ? "opacity-50 cursor-not-allowed" : has ? "hover:bg-indigo-700" : "hover:border-indigo-400 hover:text-foreground"}`}
                    >
                      {has ? <Check size={12} weight="bold" /> : <X size={12} />}
                      {data.labels[p]}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
